import { useEffect, useId, useMemo, useRef } from 'react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { cn } from '@rocco/utils/cn';
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@rocco/components/form';
import { Input } from '@rocco/components/input';
import type { DirectConversation } from '../../../types';
import { useUpdateConversation } from '../../../hooks/use-update-conversation';
import { ChatBoxSettingsButton } from './ChatBoxSettingsButton';
import { BuildHubSelect } from './BuildHubSelect';

const settingsFormSchema = z.object({
    title: z
        .string()
        .trim()
        .max(100, { message: 'Maximum 100 characters' })
        .transform((value) => value.replace(/\s+/, ' ')),
    buildHubID: z.string(),
});

/**
 * The "Settings" tab content of the chat box conversation settings.
 * @see {@link ChatBoxSettings}
 */
export function ChatBoxSettingsSettings({
    className,
    conversation,
    onClose,
}: {
    className?: string;
    conversation: DirectConversation;
    onClose?: () => void;
}) {
    const { isSubmitting, updateConversation } = useUpdateConversation({
        conversationID: Number(conversation.ID),
        onSuccess: (conversation) => {
            settingsForm.reset({
                title: conversation.Name ?? '',
                buildHubID: conversation.BuildHub?.ID ?? '',
            });
            onClose?.();
        },
    });
    const isMounted = useRef(false);
    const settingsFormReactId = useId();
    const settingsFormId = `chat-box-settings-${settingsFormReactId}`;
    const defaultValues = useMemo(() => {
        return {
            title: conversation.Name ?? '',
            buildHubID: conversation.BuildHub?.ID ?? '',
        };
    }, [conversation]);
    const settingsForm = useForm<z.infer<typeof settingsFormSchema>>({
        resolver: zodResolver(settingsFormSchema),
        mode: 'onChange',
        defaultValues,
        disabled: isSubmitting,
    });

    function onSubmit(values: z.infer<typeof settingsFormSchema>) {
        updateConversation({
            name: values.title,
            buildHubID: values.buildHubID ? Number(values.buildHubID) : 0,
        });
    }

    useEffect(() => {
        if (isMounted.current) {
            settingsForm.reset(defaultValues, { keepDirtyValues: true });
        } else {
            isMounted.current = true;
        }
    }, [settingsForm, defaultValues]);

    return (
        <Form {...settingsForm}>
            <form
                id={settingsFormId}
                className={cn('flex flex-col gap-[8px]', className)}
                onSubmit={settingsForm.handleSubmit(onSubmit)}
            >
                <div className="min-h-0 flex-1 space-y-[32px] overflow-y-auto">
                    <FormField
                        name="title"
                        control={settingsForm.control}
                        render={({ field }) => (
                            <FormItem className="space-y-[8px]">
                                <FormLabel
                                    className="ml-[4px] text-[14px]"
                                    errorVariant="errorDarker"
                                >
                                    Conversation title
                                </FormLabel>
                                <FormControl>
                                    <Input
                                        className="rounded-[8px] px-[12px] py-[8px] !text-[16px] !leading-tight md:!text-[14px]"
                                        color="contrast"
                                        text="default"
                                        placeholder="Enter a title for this conversation or group"
                                        autoFocus
                                        autoComplete="off"
                                        {...field}
                                    />
                                </FormControl>
                                <FormMessage
                                    className="text-[12px]"
                                    variant="darker"
                                />
                            </FormItem>
                        )}
                    />
                    <FormField
                        name="buildHubID"
                        control={settingsForm.control}
                        render={({ field }) => (
                            <FormItem className="space-y-[8px]">
                                <FormLabel
                                    className="ml-[4px] text-[14px]"
                                    errorVariant="errorDarker"
                                >
                                    Project
                                </FormLabel>
                                <FormControl>
                                    <BuildHubSelect
                                        ref={field.ref}
                                        currentBuildHub={conversation.BuildHub}
                                        name={field.name}
                                        value={field.value}
                                        disabled={field.disabled}
                                        onValueChange={field.onChange}
                                    />
                                </FormControl>
                            </FormItem>
                        )}
                    />
                </div>
                <div className="flex items-center justify-end gap-[4px]">
                    <ChatBoxSettingsButton
                        className="hover:bg-white/50"
                        type="button"
                        color="transparent"
                        disabled={settingsForm.formState.disabled}
                        onClick={onClose}
                    >
                        Cancel
                    </ChatBoxSettingsButton>
                    <ChatBoxSettingsButton
                        type="submit"
                        disabled={
                            settingsForm.formState.disabled ||
                            !settingsForm.formState.isDirty
                        }
                        isLoading={settingsForm.formState.disabled}
                    >
                        Save & Close
                    </ChatBoxSettingsButton>
                </div>
            </form>
        </Form>
    );
}
